"use client"

import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "next-intl"
import { Search, MapPin, Filter, X, ChevronLeft, ChevronRight, Users } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { InlineLoader } from "@/components/page-loader"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { createClient } from "@/lib/supabase/client"
import { PublicNavbar } from "@/components/public-navbar"
import { ClientFooter } from "@/components/client/client-footer"
import { useUserRole } from "@/providers/user-role-provider"
import { readClientView } from "@/lib/client-view"
import { formatCity, formatState, displayLocation } from "@/lib/format-location"
import { isBlockedBarberName } from "@/lib/discovery-blocklist"

// Cycled aspect ratios for the Discover "Feed" masonry rhythm (Pinterest-style
// variable heights). Stable per index so a tile doesn't resize between renders.
const FEED_ASPECTS = ["aspect-[4/5]", "aspect-square", "aspect-[3/4]", "aspect-[5/4]"] as const

// Availability/payment badge labels live in messages/pages/barbers.*.json
// under "card.availability" / "card.payment", keyed by the stored value.

interface BarberCard {
  id: string
  slug: string
  city: string | null
  state: string | null
  years_experience: number | null
  availability: string | null
  payment_type_preference: string | null
  is_open_to_work: boolean
  instagram_url: string | null
  tiktok_url: string | null
  profiles: { full_name: string; avatar_url: string }
  portfolio: { image_url: string }[]
  followers_count: number
}

const FILTER_AVAILABILITY = [
  { value: "", labelKey: "anyAvailability" },
  { value: "full_time", labelKey: "fullTime" },
  { value: "part_time", labelKey: "partTime" },
  { value: "weekends_only", labelKey: "weekends" },
  { value: "flexible", labelKey: "flexible" },
]

const FILTER_PAYMENT = [
  { value: "", labelKey: "anyType" },
  { value: "booth_rental", labelKey: "boothRental" },
  { value: "commission", labelKey: "commission" },
  { value: "both", labelKey: "either" },
]

export default function DiscoverArtistsPage() {
  const t = useTranslations("pages.barbers")
  const { isClient } = useUserRole()
  const [clientModeActive, setClientModeActive] = useState(false)
  useEffect(() => { setClientModeActive(readClientView()) }, [])
  const clientLens = isClient || clientModeActive
  const supabase = createClient()
  const [barbers, setBarbers] = useState<BarberCard[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [cityFilter, setCityFilter] = useState("")
  const [availabilityFilter, setAvailabilityFilter] = useState("")
  const [paymentFilter, setPaymentFilter] = useState("")
  const [openToWorkOnly, setOpenToWorkOnly] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  // Discover (client lens) can browse as Cards or an image-led Feed (masonry of
  // portfolio pics, tap → barber profile). Pros keep the focused card feed.
  const [discoverView, setDiscoverView] = useState<"cards" | "feed">("cards")

  const fetchBarbers = useCallback(async () => {
    setLoading(true)
    let query = supabase
      .from("barber_profiles")
      .select(`
        id, slug, city, state, years_experience, availability,
        payment_type_preference, is_open_to_work, instagram_url, tiktok_url,
        profiles(full_name, avatar_url)
      `)
      .eq("is_published", true)
      .eq("is_hidden", false)
      .order("created_at", { ascending: false })

    if (openToWorkOnly) query = query.eq("is_open_to_work", true)
    if (availabilityFilter) query = query.eq("availability", availabilityFilter)
    if (paymentFilter) query = query.eq("payment_type_preference", paymentFilter)
    if (cityFilter.trim()) query = query.ilike("city", `%${cityFilter.trim()}%`)

    const { data: rawData } = await query.limit(60)
    if (!rawData) { setLoading(false); return }
    // Blocklisted test/junk accounts never reach the Discover feed
    const data = rawData.filter((b: any) => !isBlockedBarberName(b.profiles?.full_name))

    // Portfolio previews + follower counts for ALL barbers in TWO bulk queries
    // (was 2 queries per barber → up to 120 round-trips). Group in memory.
    const ids = data.map((b: any) => b.id)
    const [{ data: pics }, { data: follows }] = await Promise.all([
      supabase
        .from("barber_portfolio_images")
        .select("barber_id, image_url, display_order")
        .in("barber_id", ids)
        .order("display_order", { ascending: true }),
      supabase.from("network_follows").select("following_id").in("following_id", ids),
    ])

    const picsByBarber = new Map<string, { image_url: string }[]>()
    for (const p of pics || []) {
      const arr = picsByBarber.get(p.barber_id) ?? []
      if (arr.length < 3) arr.push({ image_url: p.image_url }) // first 3 by display_order
      picsByBarber.set(p.barber_id, arr)
    }
    const followerCount = new Map<string, number>()
    for (const f of follows || []) followerCount.set(f.following_id, (followerCount.get(f.following_id) ?? 0) + 1)

    const enriched = data.map((b: any) => ({
      ...b,
      portfolio: picsByBarber.get(b.id) ?? [],
      followers_count: followerCount.get(b.id) ?? 0,
    })) as BarberCard[]

    setBarbers(enriched)
    setLoading(false)
    // NOTE: `search` is intentionally NOT a dependency — search filters the
    // already-fetched list client-side (see visibleBarbers), so typing must
    // not re-run this whole fetch.
  }, [cityFilter, availabilityFilter, paymentFilter, openToWorkOnly])

  useEffect(() => {
    const t = setTimeout(fetchBarbers, 300)
    return () => clearTimeout(t)
  }, [fetchBarbers])

  // Search filters the fetched list in memory (name match) — no refetch.
  const visibleBarbers = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return barbers
    return barbers.filter((b) => b.profiles?.full_name?.toLowerCase().includes(q))
  }, [barbers, search])

  const hasActiveFilters = openToWorkOnly || availabilityFilter || paymentFilter || cityFilter

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PublicNavbar />

      {/* Hero + search + filter. PRO lens keeps the narrow single-column feed
          (focused talent feed). CLIENT lens (Discover Barbers) opens up to a
          /listings-width directory on desktop — heading stays left — with the
          card list becoming a 3-across grid below. Desktop top padding matches
          /listings (Explore) so the heading clears the floating chrome by the
          same amount. */}
      <main className={`flex-1 w-full mx-auto px-4 md:px-6 pt-4 md:pt-20 pb-24 ${clientLens ? "max-w-xl lg:max-w-6xl" : "max-w-xl"}`}>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">{t("header.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {/* Client mode reframes the page from hiring (owner sourcing talent)
                to discovery — clients are finding someone to book, not hire. */}
            {clientLens ? "Find talented barbers & stylists." : t("header.subtitle")}
          </p>
        </div>

        {/* Search + filter block — stays compact (xl) inside the wide client
            directory so the input doesn't stretch across the whole page. */}
        <div className={clientLens ? "lg:max-w-xl" : undefined}>
        {/* Search + filter row */}
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t("search.placeholder")}
              className="pl-9 rounded-full bg-muted/50 border-border"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            className={`rounded-full flex-shrink-0 ${hasActiveFilters ? "border-foreground bg-foreground text-background" : ""}`}
            onClick={() => setShowFilters((v) => !v)}
          >
            {hasActiveFilters ? <X className="w-4 h-4" /> : <Filter className="w-4 h-4" />}
          </Button>
        </div>

        {/* Filter panel — sectioned, no outer container. Each group gets
            a small uppercase header so the panel reads as a hierarchy
            instead of stacked pill rows. */}
        {showFilters && (
          <div className="mb-6 space-y-5">
            {/* Location */}
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{t("filters.location")}</p>
              <Input
                placeholder={t("filters.cityPlaceholder")}
                className="rounded-full text-sm h-9 bg-muted/40 border-border max-w-[200px]"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{t("filters.status")}</p>
              <button
                onClick={() => setOpenToWorkOnly((v) => !v)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  openToWorkOnly
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-border hover:bg-muted"
                }`}
              >
                {t("filters.openToWork")}
              </button>
            </div>

            {/* Availability */}
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{t("filters.availability")}</p>
              <div className="flex gap-1.5 flex-wrap">
                {FILTER_AVAILABILITY.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setAvailabilityFilter((prev) => (prev === f.value ? "" : f.value))}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                      availabilityFilter === f.value
                        ? "border-foreground bg-foreground text-background"
                        : "border-border hover:bg-muted"
                    }`}
                  >
                    {t(`filters.${f.labelKey}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment */}
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{t("filters.payment")}</p>
              <div className="flex gap-1.5 flex-wrap">
                {FILTER_PAYMENT.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setPaymentFilter((prev) => (prev === f.value ? "" : f.value))}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                      paymentFilter === f.value
                        ? "border-foreground bg-foreground text-background"
                        : "border-border hover:bg-muted"
                    }`}
                  >
                    {t(`filters.${f.labelKey}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear all — right-aligned, subtle */}
            {hasActiveFilters && (
              <div className="flex justify-end pt-1 border-t border-border">
                <button
                  onClick={() => {
                    setCityFilter("")
                    setOpenToWorkOnly(false)
                    setAvailabilityFilter("")
                    setPaymentFilter("")
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-3 inline-flex items-center gap-1"
                >
                  <X className="w-3 h-3" />
                  {t("filters.clearAll")}
                </button>
              </div>
            )}
          </div>
        )}
        </div>{/* /search + filter block */}

        {!loading && (
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-xs text-muted-foreground">
              {t("count", { count: visibleBarbers.length })}
            </p>
            {/* Cards | Feed toggle — Discover (client lens) only. */}
            {clientLens && visibleBarbers.length > 0 && (
              <div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted flex-shrink-0">
                {(["cards", "feed"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setDiscoverView(v)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                      discoverView === v ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Card list — single-column feed for pros; 3-across grid for the
            client directory at desktop widths. */}
        {loading ? (
          <InlineLoader className="py-24" />
        ) : visibleBarbers.length === 0 ? (
          <div className="text-center py-24">
            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-semibold">{t("empty.title")}</p>
            <p className="text-sm text-muted-foreground mt-1">{t("empty.subtitle")}</p>
          </div>
        ) : (
          <>
            {clientLens && discoverView === "feed" ? (
              (() => {
                // Flatten every visible barber's portfolio pics into one
                // image-led masonry feed; each tile links to that barber's
                // profile (discovery by the work, not a card list).
                const feedItems = visibleBarbers.flatMap((b) =>
                  b.portfolio.map((img, i) => ({ key: `${b.id}-${i}`, img: img.image_url, barber: b })),
                )
                if (feedItems.length === 0) {
                  return (
                    <div className="text-center py-16 text-sm text-muted-foreground">
                      No photos to show yet — switch to Cards to browse barbers.
                    </div>
                  )
                }
                return (
                  <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-3 sm:gap-4">
                    {feedItems.map((it, index) => {
                      const name = it.barber.profiles?.full_name || "Barber"
                      const city = [formatCity(it.barber.city || ""), formatState(it.barber.state || "")].filter(Boolean).join(", ")
                      return (
                        <Link
                          key={it.key}
                          href={`/barbers/${it.barber.slug}`}
                          className="block break-inside-avoid mb-3 sm:mb-4 group"
                        >
                          <div className={`relative w-full overflow-hidden rounded-xl bg-muted ${FEED_ASPECTS[index % FEED_ASPECTS.length]}`}>
                            <Image
                              src={it.img}
                              alt={`Work by ${name}`}
                              fill
                              className="object-cover group-hover:scale-[1.02] transition-transform duration-300"
                              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                            <div className="absolute inset-x-0 bottom-0 p-2.5 flex items-center gap-2">
                              <Avatar className="w-6 h-6 border border-white/30 flex-shrink-0">
                                <AvatarImage src={it.barber.profiles?.avatar_url || ""} />
                                <AvatarFallback className="bg-white/20 text-white text-[10px]">{name.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <div className="min-w-0">
                                <p className="text-white text-[11px] font-semibold truncate leading-tight">{name}</p>
                                {city && <p className="text-white/80 text-[10px] truncate leading-tight">{city}</p>}
                              </div>
                            </div>
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                )
              })()
            ) : (
              <div className={`divide-y divide-border ${clientLens ? "lg:divide-y-0 lg:grid lg:grid-cols-3 lg:gap-x-8 lg:gap-y-12 lg:items-start" : ""}`}>
                {visibleBarbers.map((barber) => (
                  <PublicTalentCard key={barber.id} barber={barber} />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <ClientFooter className="mt-auto" />
    </div>
  )
}

// Public-facing talent card — mirrors the in-app TalentCard layout from
// /dashboard/owner/network, minus auth-only actions (follow / message /
// inquire). Whole card links to the barber profile; "Sign in to message"
// nudge sits where the in-app Message/Inquire pair lives.
function PublicTalentCard({ barber }: { barber: BarberCard }) {
  const t = useTranslations("pages.barbers")
  const { isClient } = useUserRole()
  const [clientModeActive, setClientModeActive] = useState(false)
  useEffect(() => { setClientModeActive(readClientView()) }, [])
  const clientLens = isClient || clientModeActive
  const availabilityLabels = t.raw("card.availability") as Record<string, string>
  const paymentLabels = t.raw("card.payment") as Record<string, string>
  const name = barber.profiles?.full_name || t("card.nameFallback")
  const location = displayLocation(barber.city, barber.state)
  const previewImages = barber.portfolio.slice(0, 6)
  const portfolioRef = useRef<HTMLDivElement>(null)

  return (
    // In the client-lens desktop grid the divide-y separators are off, so the
    // vertical padding drops too (grid gap owns the rhythm there).
    <div className={`space-y-3 py-5 ${clientLens ? "lg:py-0" : ""}`}>
      {/* Header: avatar + name + city */}
      <Link
        href={`/barbers/${barber.slug}`}
        className="flex items-center gap-3 min-w-0 hover:opacity-80 transition-opacity"
      >
        <Avatar className="w-11 h-11 border border-border flex-shrink-0">
          <AvatarImage src={barber.profiles?.avatar_url || ""} />
          <AvatarFallback className="text-sm bg-muted">{name.charAt(0)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="font-bold text-base truncate leading-tight">{name}</p>
          {location && (
            <p className="text-xs text-muted-foreground flex items-center gap-0.5 mt-0.5">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{location}</span>
            </p>
          )}
        </div>
      </Link>

      {/* Portfolio carousel — 3-up scroller with desktop chevrons */}
      {previewImages.length > 0 && (
        <div className="relative group">
          {previewImages.length > 3 && (
            <button
              type="button"
              onClick={() => portfolioRef.current?.scrollBy({ left: -260, behavior: "smooth" })}
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-background/95 border border-border shadow-md items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity -ml-3"
              aria-label={t("card.prevAria")}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          <div
            ref={portfolioRef}
            className="flex gap-2 overflow-x-auto scrollbar-hide snap-x snap-mandatory touch-pan-x pb-1"
          >
            {previewImages.map((img, i) => (
              <Link
                key={i}
                href={`/barbers/${barber.slug}`}
                className="relative flex-shrink-0 w-[calc((100%-1rem)/3)] aspect-square overflow-hidden rounded-xl bg-muted snap-start"
              >
                <Image
                  src={img.image_url}
                  alt=""
                  fill
                  className="object-cover hover:scale-105 transition-transform duration-300"
                  sizes="(max-width: 640px) 33vw, 200px"
                />
              </Link>
            ))}
          </div>
          {previewImages.length > 3 && (
            <button
              type="button"
              onClick={() => portfolioRef.current?.scrollBy({ left: 260, behavior: "smooth" })}
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-background/95 border border-border shadow-md items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity -mr-3"
              aria-label={t("card.nextAria")}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Badges row — client-facing, so the hiring emblems (open-to-work,
          availability, payment preference) are intentionally hidden. Only the
          experience pill remains. */}
      {barber.years_experience != null && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-normal bg-muted text-foreground border border-border px-3 py-1 rounded-full whitespace-nowrap tabular-nums">
            {t("card.yearsExp", { years: barber.years_experience })}
          </span>
        </div>
      )}

      {/* CTA row. Clients don't message barbers (we use call/text/WhatsApp on
          the profile), so a signed-in client gets a "View profile" link here.
          Logged-out visitors keep the signup nudge. */}
      <div className="flex justify-end items-center gap-5 pt-1 pr-2">
        <Link
          href={clientLens ? `/barbers/${barber.slug}` : "/auth/signup"}
          className="inline-flex items-center gap-1.5 text-xs font-light text-emerald-700 dark:text-emerald-500 hover:opacity-70 transition-opacity"
        >
          {clientLens ? "View Barber" : t("card.signInToMessage")}
          <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  )
}
